import { useMemo, useState } from 'react';
import { openAiProductNameAPI } from '../../../context-api/api-services/OpenAi.services';

const productNameAiCache = new Map();

export function useProductNameAiDialog({
  openProductNameDialog,
  setOpenProductNameDialog,
  setProductName,
  setSelectedTitle,
  title = '',
  productCategory = 'select',
  cuisineType = '',
}) {
  const hasCategory = productCategory && productCategory !== 'select';
  const hasCuisine = !!cuisineType && cuisineType !== 'select';

  const baseContext = useMemo(() => {
    const safeTitle = title?.trim() || 'your dish';
    const parts = [`dish called "${safeTitle}"`];

    if (hasCategory) {
      parts.push(`in the "${productCategory}" category`);
    }
    if (hasCuisine) {
      parts.push(`from ${cuisineType} cuisine`);
    }

    return parts.join(', ');
  }, [title, hasCategory, hasCuisine, productCategory, cuisineType]);

  const predefinedPrompts = useMemo(
    () => [
      {
        id: 'default',
        label: 'Balanced & descriptive',
        text: `Suggest creative, short, and descriptive product names for a ${baseContext}. Focus on clarity and menu readability. Return only the names, one per line, without numbering.`,
      },
      {
        id: 'premium',
        label: 'Premium restaurant style',
        text: `Suggest modern, premium restaurant-style names for a ${baseContext}. Make them feel upscale but still easy to understand. Return only the names, one per line, without numbering.`,
      },
      {
        id: 'fun',
        label: 'Fun & catchy',
        text: `Suggest fun, catchy, and memorable product names for a ${baseContext}, suitable for online delivery apps. Keep them short and brandable. Return only the names, one per line, without numbering.`,
      },
    ],
    [baseContext]
  );

  const [activePrompt, setActivePrompt] = useState(predefinedPrompts[0].text);
  const [chosenTitle, setChosenTitle] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  const chatResponseText = useMemo(() => {
    if (!suggestions.length) return '';
    return suggestions
      .map((s, index) => {
        const t = s?.title || '';
        const meta = s?.meta_title || '';
        const keywords = Array.isArray(s?.seo_keywords) ? s.seo_keywords.join(', ') : '';
        let block = `${index + 1}. ${t}`;
        if (meta) {
          block += `\n   Meta title: ${meta}`;
        }
        if (keywords) {
          block += `\n   SEO keywords: ${keywords}`;
        }
        return block;
      })
      .join('\n\n');
  }, [suggestions]);

  const handleClose = () => {
    setOpenProductNameDialog(false);
  };

  const handleSelectPrompt = (promptText) => {
    setActivePrompt(promptText);
  };

  const handleGenerateTitles = async () => {
    if (!activePrompt?.trim()) return;
    const promptProductName = title?.trim() || 'your dish';

    const payload = {
      promptProductName,
      prompt: activePrompt,
      productCategory: hasCategory ? productCategory : 'main course',
      cuisine: hasCuisine ? cuisineType : 'indian',
    };

    const cacheKey = JSON.stringify(payload);

    // Check cache first – if we have a cached response for this
    // exact payload, reuse it instead of hitting the API.
    if (productNameAiCache.has(cacheKey)) {
      const cachedSuggestions = productNameAiCache.get(cacheKey) || [];
      setErrorMessage('');
      setSuggestions(cachedSuggestions);
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setSuggestions([]);
    try {
      const response = await openAiProductNameAPI(payload);
      if (response?.success === false || response?.error) {
        setErrorMessage(
          response?.error?.message || response?.data?.error || 'Something went wrong. Please try again.'
        );
        return;
      }
      if (response?.data?.success && Array.isArray(response.data.suggestions)) {
        const nextSuggestions = response.data.suggestions.slice(0, 4);
        // Save to cache for this exact payload so the next
        // identical request is served from memory.
        productNameAiCache.set(cacheKey, nextSuggestions);
        setSuggestions(nextSuggestions);
      } else {
        setErrorMessage(response?.data?.error || 'Could not get suggestions. Please try again.');
      }
    } catch (err) {
      setErrorMessage(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChooseSuggestion = (value) => {
    setChosenTitle(value);
  };

  const handleSelectTitle = () => {
    if (!chosenTitle?.trim()) return;
    setProductName(chosenTitle.trim());
    if (setSelectedTitle) {
      setSelectedTitle(chosenTitle.trim());
    }
    setOpenProductNameDialog(false);
  };

  return {
    hasCategory,
    hasCuisine,
    predefinedPrompts,
    activePrompt,
    setActivePrompt,
    chosenTitle,
    isLoading,
    suggestions,
    errorMessage,
    chatResponseText,
    handleClose,
    handleSelectPrompt,
    handleGenerateTitles,
    handleChooseSuggestion,
    handleSelectTitle,
  };
}
